import React, { useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import RecordTypeSelect from '../../components/Records/RecordTypeSelect';
import * as RecordActions from '../../actions/Records/actions';
import * as UserActions from '../../actions/User/actions';
import { RootState } from '../../store/configureStore';
import Loading from '../../components/common/Loading';

interface RouteParams {
  institutionId: string;
  userId: string;
}

interface StateProps {
  institution: any;
  userListData: any[];
  counts: any;
}

interface DispatchProps {
  recordActions: typeof RecordActions;
  userActions: typeof UserActions;
}

type Props = StateProps & DispatchProps;

const RecordTypeSelectContainer: React.FC<Props> = ({
  institution,
  userListData,
  counts,
  recordActions,
  userActions,
}) => {
  const { institutionId, userId } = useParams<RouteParams>();
  const initializedRef = useRef(false);
  const userFetchedRef = useRef(false);

  // ユーザー情報を取得
  const user = userListData.find((u: any) => u.user_id === userId);
  const uniqueId = user?.unique_id;

  // ユーザーデータがない場合は取得
  useEffect(() => {
    if (userListData.length === 0 && !userFetchedRef.current && institutionId) {
      userFetchedRef.current = true;
      userActions.getUsersWithUniqueId(institutionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionId, userListData.length]);

  // 初期化処理（一度だけ実行）
  useEffect(() => {
    if (uniqueId && !initializedRef.current) {
      initializedRef.current = true;
      recordActions.setCurrentUser({
        unique_id: uniqueId,
        userId: user.user_id,
        userName: user.user_name,
        institutionId: institutionId,
        institutionName: institution?.institution_name || '',
      });
      recordActions.fetchAllRecordCounts(uniqueId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId]);

  // useCallbackでメモ化（再レンダリング時に同じ参照を維持）
  const handleFetchCounts = useCallback(
    (id: string) => {
      recordActions.fetchAllRecordCounts(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ユーザー情報がない場合はローディング
  if (!user) {
    return <Loading />;
  }

  return (
    <RecordTypeSelect
      institutionId={institutionId}
      institutionName={institution?.institution_name || ''}
      userId={userId}
      userName={user.user_name}
      uniqueId={user.unique_id || ''}
      counts={counts}
      onFetchCounts={handleFetchCounts}
    />
  );
};

function mapStateToProps(state: RootState): StateProps {
  return {
    institution: state.institution.institution,
    userListData: state.user.listData,
    counts: state.records.counts,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    recordActions: bindActionCreators(RecordActions, dispatch),
    userActions: bindActionCreators(UserActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(RecordTypeSelectContainer);
